import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ServerStatusService {
  private isOnlineSubject = new BehaviorSubject<boolean>(true);
  public isOnline$: Observable<boolean> = this.isOnlineSubject.asObservable();

  private pollingIntervalId: any = null;
  private httpClient: HttpClient;

  // Use HttpBackend to create an HttpClient that completely bypasses interceptors
  constructor(private ngZone: NgZone, private httpBackend: HttpBackend) {
    this.httpClient = new HttpClient(this.httpBackend);
  }

  public get isOnline(): boolean {
    return this.isOnlineSubject.value;
  }

  /**
   * Sets the status to offline and initiates a background heartbeat ping to detect when the server recovers.
   */
  public setOffline(): void {
    if (this.isOnlineSubject.value) {
      this.isOnlineSubject.next(false);
      console.warn('Server detected offline. Starting connection heartbeat...');
      this.startHeartbeat();
    }
  }

  /**
   * Sets the status to online and stops the heartbeat ping.
   */
  public setOnline(): void {
    if (!this.isOnlineSubject.value) {
      this.isOnlineSubject.next(true);
      console.log('Server detected online. Heartbeat stopped.');
      this.stopHeartbeat();
    }
  }

  private startHeartbeat(): void {
    if (this.pollingIntervalId) {
      return;
    }

    // Run outside Angular's zone to prevent change detection runs on every tick
    this.ngZone.runOutsideAngular(() => {
      this.pollingIntervalId = setInterval(() => {
        this.pingServer();
      }, 10000); // Poll every 10 seconds
    });
  }

  private stopHeartbeat(): void {
    if (this.pollingIntervalId) {
      clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = null;
    }
  }

  /**
   * Performs an immediate one-shot server connectivity check.
   * Updates the online/offline state based on the result.
   * Can be called from any component to trigger an upfront check (e.g. on login page load).
   */
  public checkConnection(): void {
    this.httpClient.get(`${environment.server_url}/ping`, { responseType: 'text' }).subscribe({
      next: (response) => {
        if (response === 'pong') {
          this.ngZone.run(() => this.setOnline());
        }
      },
      error: () => {
        this.ngZone.run(() => this.setOffline());
      }
    });
  }

  private pingServer(): void {
    // We use responseType: 'text' because the backend returns a plain string "pong"
    this.httpClient.get(`${environment.server_url}/ping`, { responseType: 'text' }).subscribe({
      next: (response) => {
        if (response === 'pong') {
          // Bring execution back into Angular zone to update state and trigger UI changes
          this.ngZone.run(() => {
            this.setOnline();
          });
        }
      },
      error: (err) => {
        console.log('Heartbeat ping failed:', err.message || err.statusText);
      }
    });
  }
}
